"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { authenticatedRawFetch } from "@/lib/api/client";
import {
  getAttentionCountsQueryKey,
  getStreamProjectConversationsUrl,
} from "@/lib/api/generated";
import {
  isTerminalAuthError,
  parseSSE,
} from "@/features/jobs/hooks/use-job-pipeline-events";
import { SCHEDULE_QUERY_PREFIX } from "./use-schedule";
import { CONVERSATIONS_QUERY_PREFIX } from "../lib/query-keys";
import {
  useConversationsStore,
  type AttentionEvent,
  type ParticipantsEvent,
  type StateEvent,
} from "../store/conversations-store";

const MAX_ATTEMPTS = 10;
// The server emits a `: keep-alive` comment every ~15s when idle. Anything
// quieter than 3x that is treated as a dead connection and reconnected.
const HEARTBEAT_TIMEOUT_MS = 45_000;
// Calendar webhooks arrive in bursts. One schedule refetch covers the burst.
const SCHEDULE_REFETCH_DEBOUNCE_MS = 300;

const EVENT_TYPES: ReadonlySet<string> = new Set([
  "conversation.state",
  "conversation.attention",
  "conversation.participants",
]);

const ORG_LIMIT_MIN_MS = 30_000;
const ORG_LIMIT_MAX_MS = 120_000;

const isStateEvent = (v: unknown): v is StateEvent =>
  typeof (v as StateEvent)?.conversation_id === "string" &&
  typeof (v as StateEvent)?.state === "string";
const isAttentionEvent = (v: unknown): v is AttentionEvent =>
  typeof (v as AttentionEvent)?.conversation_id === "string" &&
  Array.isArray((v as AttentionEvent)?.entered) &&
  Array.isArray((v as AttentionEvent)?.left);
const isParticipantsEvent = (v: unknown): v is ParticipantsEvent =>
  typeof (v as ParticipantsEvent)?.conversation_id === "string" &&
  Array.isArray((v as ParticipantsEvent)?.participants);

/**
 * Subscribes to `GET /api/projects/{projectId}/conversations/stream` (SSE),
 * one connection per active project, and feeds `conversation.state` /
 * `conversation.attention` / `conversation.participants` events into the
 * conversations store. On
 * `stream_ready` it marks the stream connected and invalidates the list query
 * prefix plus the unscoped attention-counts prefix so job/candidate counts
 * refresh too, and missed events while we were away are reconciled from REST.
 * That invalidation is deliberately
 * unconditional (no `hasConnected` skip): this stream has no replay, so
 * events between the list hydrate and `stream_ready` are lost unless we
 * refetch, and it is the backstop for a list query that hydrated before the
 * stream connected. Heartbeat comment frames (`: keep-alive`) and unknown
 * event families are ignored. There are no toasts in this slice.
 *
 * Reconnects with capped exponential backoff; a heartbeat watchdog forces an
 * immediate reconnect if the stream goes silent. A 429 with `ORG_STREAM_LIMIT`
 * waits 30–120 s (jittered) without consuming an attempt; that wait uses the
 * same abortable timer as backoff so unmount clears it and `online` can cut
 * it short.
 *
 * A stream that gave up (backoff exhausted, or a 401 while the session was
 * being refreshed) is not dead for good: coming back `online` or returning to
 * the tab restarts it with a fresh attempt budget; `online` also drops a
 * connection that still looks open (a half-open socket after a network change
 * would otherwise only be caught by the watchdog) and reconnects at once.
 * `connected` is published on the conversations store (`streamConnected`, with
 * the project it covers as `streamProjectId`). Changing project runs
 * `resetForProject` at the top of the new effect so rows the new project's
 * list query already hydrated are kept.
 */
export function useProjectConversationsStream(projectId: string | null): {
  connected: boolean;
} {
  const connected = useConversationsStore((s) => s.streamConnected);
  const setConnected = useConversationsStore((s) => s.setStreamConnected);
  const applyStateEvent = useConversationsStore((s) => s.applyStateEvent);
  const applyAttentionEvent = useConversationsStore((s) => s.applyAttentionEvent);
  const applyParticipantsEvent = useConversationsStore((s) => s.applyParticipantsEvent);
  const qc = useQueryClient();

  useEffect(() => {
    // No project: nothing to stream. `connected` is already false here, either
    // from the initial state or from the previous project's cleanup below.
    if (!projectId) return;
    const pid: string = projectId;
    useConversationsStore.getState().resetForProject(pid);

    let cancelled = false;
    let attempt = 0;
    let isConnected = false;
    // True while `run()` is executing (connected, reading, or backing off).
    let loopAlive = false;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    // Resolves the pending backoff / org-limit wait early (wake-up on `online` / focus).
    let skipBackoff: (() => void) | null = null;
    let activeController: AbortController | null = null;
    // Set when `online` aborts a live connection so the loop skips the backoff.
    let reconnectRequested = false;
    let scheduleTimer: ReturnType<typeof setTimeout> | null = null;
    const refetchSchedule = () => {
      if (scheduleTimer) clearTimeout(scheduleTimer);
      scheduleTimer = setTimeout(() => {
        scheduleTimer = null;
        void qc.invalidateQueries({ queryKey: [SCHEDULE_QUERY_PREFIX] });
      }, SCHEDULE_REFETCH_DEBOUNCE_MS);
    };

    const publishConnected = (value: boolean) => {
      isConnected = value;
      setConnected(value, value ? pid : null);
    };

    const waitMs = (ms: number) =>
      new Promise<void>((resolve) => {
        skipBackoff = () => {
          if (backoffTimer) clearTimeout(backoffTimer);
          backoffTimer = null;
          skipBackoff = null;
          resolve();
        };
        backoffTimer = setTimeout(() => {
          backoffTimer = null;
          skipBackoff = null;
          resolve();
        }, ms);
      });

    async function run() {
      loopAlive = true;
      try {
        while (!cancelled) {
          const controller = new AbortController();
          activeController = controller;
          let watchdog: ReturnType<typeof setTimeout> | null = null;
          let watchdogTripped = false;
          const bumpWatchdog = () => {
            if (watchdog) clearTimeout(watchdog);
            watchdog = setTimeout(() => {
              watchdogTripped = true;
              controller.abort();
            }, HEARTBEAT_TIMEOUT_MS);
          };

          try {
            const res = await authenticatedRawFetch(
              getStreamProjectConversationsUrl(pid),
              { method: "GET", signal: controller.signal, cache: "no-store" },
            );
            if (!res.ok || !res.body) {
              if (res.status === 401) {
                publishConnected(false);
                return;
              }
              if (res.status === 429) {
                let code = "";
                try {
                  code =
                    ((await res.json()) as { data?: { code?: string } })?.data
                      ?.code ?? "";
                } catch {
                  /* unreadable envelope */
                }
                if (code === "ORG_STREAM_LIMIT") {
                  publishConnected(false);
                  await waitMs(
                    ORG_LIMIT_MIN_MS +
                      Math.random() * (ORG_LIMIT_MAX_MS - ORG_LIMIT_MIN_MS),
                  );
                  if (cancelled) return;
                  continue;
                }
              }
              throw new Error(`stream failed: ${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            bumpWatchdog();

            while (!cancelled) {
              const { done, value } = await reader.read();
              if (done) break;
              // Any byte, event or `: keep-alive`, proves the stream is alive.
              bumpWatchdog();
              buffer += decoder.decode(value, { stream: true });
              const parsed = parseSSE(buffer);
              buffer = parsed.rest;
              for (const e of parsed.events) {
                if (e.event === "stream_ready") {
                  attempt = 0;
                  publishConnected(true);
                  void qc.invalidateQueries({
                    queryKey: CONVERSATIONS_QUERY_PREFIX,
                  });
                  void qc.invalidateQueries({
                    queryKey: getAttentionCountsQueryKey(),
                  });
                  refetchSchedule();
                  continue;
                }
                if (e.event === "schedule.changed") {
                  refetchSchedule();
                  continue;
                }
                if (!EVENT_TYPES.has(e.event)) continue;
                let data: unknown;
                try {
                  data = JSON.parse(e.data);
                } catch {
                  continue; // unparseable payload: skip, keep the stream
                }
                if (e.event === "conversation.state") {
                  if (!isStateEvent(data)) continue;
                  applyStateEvent(data);
                  if (data.state === "scheduled" || data.state === "cancelled") {
                    refetchSchedule();
                  }
                } else if (e.event === "conversation.attention") {
                  if (!isAttentionEvent(data)) continue;
                  applyAttentionEvent(data);
                } else {
                  if (!isParticipantsEvent(data)) continue;
                  applyParticipantsEvent(data);
                }
              }
            }

            // Server closed the stream cleanly; fall through to reconnect.
            publishConnected(false);
          } catch (err) {
            if ((err as Error).name === "AbortError") {
              // Unmount teardown (cancelled) or a watchdog-forced reconnect.
              if (cancelled) return;
              publishConnected(false);
            } else if (isTerminalAuthError(err)) {
              publishConnected(false);
              return;
            } else {
              publishConnected(false);
            }
          } finally {
            if (watchdog) clearTimeout(watchdog);
          }

          if (cancelled) return;
          attempt++;
          if (attempt > MAX_ATTEMPTS) return;
          // Forced reconnects (watchdog, `online`) retry immediately; network
          // errors back off.
          const delay =
            watchdogTripped || reconnectRequested
              ? 0
              : Math.min(15_000, 1_000 * 2 ** (attempt - 1));
          reconnectRequested = false;
          await waitMs(delay);
        }
      } finally {
        loopAlive = false;
      }
    }

    // The network came back or the tab is visible again: a stream that gave
    // up gets a fresh attempt budget, one mid-backoff retries right away. A
    // stream that still looks connected is left alone on a plain visibility
    // change, but `online` drops it: after a network change the socket may be
    // half-open, which only the heartbeat watchdog would notice, 45s later.
    const wake = ({ forceReconnect = false } = {}) => {
      if (cancelled) return;
      if (isConnected) {
        if (forceReconnect) {
          reconnectRequested = true;
          activeController?.abort();
        }
        return;
      }
      attempt = 0;
      if (!loopAlive) {
        void run();
      } else {
        skipBackoff?.();
      }
    };
    const onOnline = () => wake({ forceReconnect: true });
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") wake();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);

    void run();

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (scheduleTimer) clearTimeout(scheduleTimer);
      activeController?.abort();
      if (backoffTimer) clearTimeout(backoffTimer);
      publishConnected(false);
    };
  }, [projectId, qc, applyStateEvent, applyAttentionEvent, applyParticipantsEvent, setConnected]);

  return { connected };
}
